<template lang="pug">
.catalog-header-bar
  .top.flex.inner-gap(:class="{searching}")
    .logo
      landing-link(path="/" title="Pod")
        img(src="/img/logo.svg?pod")
      subscription-tag.badge(
        v-if="showSubscriptionTag"
        is-link
        :id="highestSubscriptionBadge")

    .search
      client-only
        search-box(ref="search")

    a.search-toggler(href="#" @click.prevent="searching = !searching")
      i.pf-icon.bx.bx-x(v-if="searching")
      i.pf-icon.bx.bx-search(v-else)

    .notification(v-if="showNotificationBox")
      notification-box

    .user
      user-box

  .bottom.flex.inner-gap
    .expander

    .menu
      nuxt-link(
        :to="$links.catalog()"
        title="Catalog"
        v-click="() => $amplitude.track('CLICK_CATALOG')") Catalog

      nuxt-link(
        :to="{ name: 'brands-new' }"
        title="New Brands"
        v-click="() => $amplitude.track('CLICK_NEW_BRANDS')") New Brands

      nuxt-link(
        :to="{ name: 'products-new' }"
        title="New Products"
        v-click="() => $amplitude.track('CLICK_NEW_PRODUCTS')") New Products

      template(v-if="isVendor")
        a(:href="`${SUPPORT_URL}`" target="_blank" @click="$amplitude.track('CLICK_BRAND_GUIDE')") Support
      template(v-if="isHeadBuyer || (isBuyer && !isPreApprovalBuyer)")
        nuxt-link(
          v-if="!isHeadBuyer"
          :to="{ name: 'promotions' }"
          title="promotions"
          v-click="() => $amplitude.track('CLICK_PROMOTIONS')") Promotions
        nuxt-link(
          :to="{ name: 'order-guide' }"
          title="Order guide"
          v-click="() => $amplitude.track('CLICK_ORDER_GUIDE')") Order guide
  categories-bar(v-if="showCategories")
</template>

<script>
import { mapGetters } from 'vuex'
import NotificationBox from './NotificationBox'
import SearchBox from './SearchBox'
import UserBox from './UserBox'
import CatalogCard from './CatalogCard'
import CategoriesBar from '~/components/menus/CategoriesBar'
import SubscriptionTag from '~/components/tags/SubscriptionTag'
import { SUPPORT_URL, SUBSCRIPTION_PLAN_IDS } from '~/settings'

export default {
  components: {
    SubscriptionTag,
    CategoriesBar,
    NotificationBox,
    SearchBox,
    UserBox,
    CatalogCard
  },

  props: {
    showCategories: {
      type: Boolean,
      default: true
    },
    showNotification: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      searching: false,
      state: null,
      showStates: false,
      SUPPORT_URL
    }
  },

  computed: {
    ...mapGetters({
      addressStates: 'master/addressStates',
      isBroker: 'authentication/isBroker',
      isVendor: 'authentication/isVendor',
      isVendorAdmin: 'authentication/isVendorAdmin',
      vendorPermissionRoutes: 'authentication/vendorPermissionRoutes',
      isHeadBuyer: 'authentication/isHeadBuyer',
      isBuyer: 'authentication/isBuyer',
      isPreApprovalBuyer: 'authentication/isPreApprovalBuyer',
      categories: 'master/productCategories',
      highestSubscriptionBadge: 'authentication/highestSubscriptionBadge'
    }),

    showNotificationBox () {
      return this.showNotification && !this.isBroker && (this.isVendorAdmin || this.vendorPermissionRoutes.includes('vendors-notifications'))
    },

    showSubscriptionTag () {
      return this.highestSubscriptionBadge !== SUBSCRIPTION_PLAN_IDS.FREE
    }
  },

  watch: {
    searching (val) {
      if (!val) return
      this.$nextTick(() => {
        this.$refs.search && this.$refs.search.focus()
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.catalog--popover {
  position: absolute;
  top: calc(100% + 1px);
  left: 0;
  width: 100%;
  height: fit-content;
  z-index: 9999;
  background: white;
  padding: 1em 2em 1em 0;
  border-radius: 4px;
  border: $--border-light;
  overflow-x: auto;

  @include media(lg) {
    padding-left: 240px;
  }

  .catalog--content {
    min-width: 1000px;
  }

  .title {
    display: block;
    font-weight: bold;
    margin-bottom: 1em;
  }

  .categories {
    border-right: $--border-light;
    max-height: 650px;
    overflow-y: auto;
  }

  .sub-cate {
    min-height: 30px;
  }

  .content {
    &--item {
      display: flex;
      align-items: center;
      padding: 6px 0 6px 12px;
      border-radius: 4px;
      cursor: pointer;

      &:hover {
        color: $--color-primary;
        font-weight: $--font-weight-bold;
      }

      .name {
        margin-left: 8px;
      }

      .icon {
        width: 28px;
        height: 28px;
        mask-image: url(/img/icon.categories@2x.webp);
        mask-repeat: no-repeat;
        mask-position: top left;
        mask-size: 280px;
        background: $--color-primary;
        @mixin cell($row, $col) {
          mask-position: top ($row * -28px) left ($col * -28px);
        }
        &--all { @include cell(0, 0); }
        &--baby-child { @include cell(0, 1); }
        &--bakery { @include cell(0, 2); }
        &--beverage { @include cell(0, 3); }
        &--cheese { @include cell(0, 4); }
        &--chill { @include cell(0, 5); }
        &--confections { @include cell(0, 6); }
        &--dairy { @include cell(0, 7); }
        &--fresh { @include cell(0, 8); }
        &--frozen { @include cell(0, 9); }
        &--grab-and-go { @include cell(1, 0); }
        &--grocery { @include cell(1, 1); }
        &--hemp { @include cell(1, 2); }
        &--household { @include cell(2, 3); }
        &--juice { @include cell(1, 4); }
        &--meat { @include cell(1, 5); }
        &--new { @include cell(1, 6); }
        &--prepared { @include cell(1, 7); }
        &--prepared-foods { @include cell(1, 8); }
        &--produce { @include cell(1, 9); }
        &--snacks { @include cell(2, 0); }
        &--wellness { @include cell(2, 1); }
        &--hba { @include cell(2, 1); }
        &--vms { @include cell(2, 2); }
      }

      &.active {
        background: $--color-primary;
        color: white;
        .icon {
          background: white;
        }
      }

      .link {
        display: flex;
        align-items: center;
        width: 100%;
      }
    }
  }

  .brand-card, .product-card {
    background: #f4f6f8;

    .catalog--grid {
      display: grid;
      grid-gap: 12px 10px;
      grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
      grid-auto-rows: 0;
      grid-template-rows: 1fr;
    }
  }
}
</style>

<style lang="scss" scoped>
@keyframes bubble {
  0% { transform: scale(1); }
  20% { transform: scale(1.5); }
  100% { transform: scale(1); }
}

@keyframes textclip {
  to {
    background-position: 200% center;
  }
}
.catalog-header-bar {
  $divider: $--border-light;
  position: relative;
  background: rgba(white, .95);
  border-bottom: $divider;
  a {
    outline: none;
  }
  .top {
    align-items: center;
    min-height: 50px;
    @include media(md) {
      min-height: 58px;
    }
    &.searching {
      .logo {
        display: none;
      }
      .search {
        display: block;
      }
      .user {
        display: none;
      }
    }
    .logo {
      position: relative;
      padding: 10px 0;
      flex: 1;
      @include media(sm) {
        flex: 0 120px;
        margin-right: 20px;
      }
      @include media(md) {
        flex: 200px 0;
        margin-right: 30px;
      }
      img {
        display: block;
        width: 120px;
        @include media(md) {
          width: 160px;
        }
      }
      .badge {
        position: absolute;
        right: 0;
        top: 15px;
      }
    }
    .search {
      flex: 1;
      display: none;
      @include media(sm) {
        display: block;
      }
    }
    .search-toggler {
      display: block;
      padding: 12px;
      margin-right: -10px;
      @include media(sm) {
        display: none;
      }
      > span {
        cursor: pointer;
      }
    }
    .user {
      margin-left: 10px;
      text-align: right;
      @include media(md) {
        margin-left: 1em;
      }
    }
    .notification {
      margin-left: 10px;
      text-align: right;
      @include media(md) {
        margin-left: 1em;
      }
    }
  }
  .bottom {
    border-top: $divider;
    font-size: 12px;
    padding-left: 0;
    padding-right: 0;

    @include media(md) {
      font-size: 13px;
    }
    .expander {
      margin-right: 1.5em;
      display: none;
      justify-content: center;
      align-items: center;

      @include media(md) {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        margin-right: 0;
        flex: 0 260px;
      }
    }
    .menu {
      display: flex;
      overflow-x: scroll;
      white-space: nowrap;
      @include no-scroller();
      > a {
        &.invite-brands {
          color: $--color-primary;
        }
      }
      > * {
        min-width: auto;
        padding: .6em 0;
        color: $--color-text-regular;
        line-height: 16px;
        @include media(sm-down) {
          &:first-child {
            margin-left: 1em;
          }
          &:last-child {
            margin-right: 1em;
          }
        }
        a {
          color: $--color-text-regular;
        }
        &:not(:last-child) {
          margin-right: 1.5em;
        }
        &:hover {
          color: $--color-primary;
        }
      }
    }
  }
  .address-state-select {
    font-size: 13px;
    ::v-deep {
      .el-select {
        &__caret {
          line-height: 14px;
        }
      }
      .el-input {
        display: flex;
        align-items: center;
        > * {
          min-width: 0;
          height: 36px;
        }
        input {
          order: 2;
          font-size: 13px;
        }
        &__prefix {
          position: relative;
          font-size: 13px;
          text-align: left;
          white-space: nowrap;
          margin-right: .5em;
          display: flex;
          align-items: center;
        }
        &__inner {
          border: none;
          padding: 0;
        }
      }
    }
  }
}
</style>
